import {Field} from '../types';

export interface DevicePosition {
    latitude: number;
    longitude: number;
    /** Radius of the reading in metres, as the device reports it. */
    accuracy: number;
}

export type GpsCoordinate = 'latitude' | 'longitude';

const COORDINATE_OF_FIELD_TYPE: Record<string, GpsCoordinate> = {
    'gps-latitude': 'latitude',
    'gps-longitude': 'longitude',
};

/** Which coordinate the device fills into this field, or null for an ordinary field. */
export const gpsCoordinateOf = (field: Pick<Field, 'fieldType'>): GpsCoordinate | null =>
    (field.fieldType && COORDINATE_OF_FIELD_TYPE[field.fieldType]) || null;

/** The values a reading gives to the GPS fields of a form, keyed by field code. */
export const positionValues = (
    fields: Pick<Field, 'code' | 'fieldType'>[],
    position: DevicePosition,
): Record<string, number> => Object.fromEntries(
    fields.flatMap((field) => {
        const coordinate = gpsCoordinateOf(field);
        return coordinate ? [[field.code, position[coordinate]]] : [];
    }),
);

/**
 * Required GPS fields the device has not filled in. A form with any of them
 * cannot be sent: nobody can type the position, the device has to give it.
 */
export const missingRequiredPositions = <F extends Pick<Field, 'code' | 'required' | 'fieldType'>>(
    fields: F[],
    values: Record<string, any>,
): F[] => fields.filter((field) => {
    const value = values[field.code];
    return field.required && gpsCoordinateOf(field) !== null && (value === undefined || value === null || value === '');
});

// A form holds the two coordinates in two fields; both must come from one
// reading, so a reading is shared while it is under way and briefly afterwards.
const SHARED_FOR_MS = 10000;
let pending: Promise<DevicePosition> | null = null;
let last: { at: number; position: DevicePosition } | null = null;

/** Why there is no position: a sentence for the person, the browser's own answer, and what trying again takes. */
export class DevicePositionError extends Error {
    constructor(
        message: string,
        /** What the browser itself answered, word for word. */
        readonly browserAnswer: string,
        /** Asking again on this page cannot help - only the page loaded anew is asked again. */
        readonly needsFreshPage: boolean = false,
    ) {
        super(message);
    }
}

/** What went wrong, said to the person filling in the form - with what to do about it. */
export const positionErrorMessage = (code: number | null): string => {
    switch (code) {
        case 1:
            return 'Poloha nie je povolená. Povoľte ju prehliadaču v nastaveniach zariadenia a tejto stránke v prehliadači, potom obnovte stránku.';
        case 2:
            return 'Zariadenie nevie zistiť polohu. Zapnite zisťovanie polohy a skúste znova.';
        case 3:
            return 'Zisťovanie polohy trvalo príliš dlho. Skúste znova.';
        default:
            return 'Polohu sa nepodarilo zistiť. Skúste znova.';
    }
};

// A browser that is asking the person, or that may not ask at all, calls back
// only once it has its answer - maybe never; the timeout it is given runs only
// after it is allowed to read. The form stops waiting after this long.
export const UNANSWERED_AFTER_MS = 20000;

export const unansweredPosition = (): DevicePositionError => new DevicePositionError(
    'Prehliadač polohu zatiaľ neposkytol. Ak sa pýta na povolenie, potvrďte ho. Inak skontrolujte, či je poloha v zariadení zapnutá a tomuto prehliadaču povolená, a obnovte stránku.',
    `žiadna odpoveď do ${UNANSWERED_AFTER_MS / 1000} s`,
    true,
);

// Seven decimals are about a centimetre - more is noise of the float.
const rounded = (degrees: number): number => Math.round(degrees * 1e7) / 1e7;

/**
 * The position of the device. Rejects with a DevicePositionError.
 *
 * The Permissions API is deliberately not consulted: Safari reports "denied"
 * while it is showing its question, and a refusal comes through the error
 * callback within milliseconds anyway.
 */
export const readDevicePosition = (): Promise<DevicePosition> => {
    if (last && Date.now() - last.at < SHARED_FOR_MS) {
        return Promise.resolve(last.position);
    }
    if (pending) {
        return pending;
    }

    pending = new Promise<DevicePosition>((resolve, reject) => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            reject(new DevicePositionError('Tento prehliadač nevie zistiť polohu zariadenia.', 'navigator.geolocation chýba'));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            ({coords}) => resolve({
                latitude: rounded(coords.latitude),
                longitude: rounded(coords.longitude),
                accuracy: coords.accuracy,
            }),
            // Safari keeps a refusal for as long as the page lives, whatever is allowed in the device meanwhile.
            (error) => reject(new DevicePositionError(
                positionErrorMessage(error.code),
                `kód ${error.code}: ${error.message}`,
                error.code === 1,
            )),
            {enableHighAccuracy: true, timeout: 15000, maximumAge: 0},
        );
    })
        .then((position) => {
            last = {at: Date.now(), position};
            return position;
        })
        .finally(() => {
            pending = null;
        });

    return pending;
};

/** Forget the shared reading - the next call asks the device again. */
export const forgetDevicePosition = (): void => {
    last = null;
};
